import { TDeviceType, useDeviceType } from "@cian/ui-kit";
import * as React from 'react';
import { connect } from 'react-redux';
import { ca } from '@cian/analytics';
import { RouteComponentProps, useLocation } from "react-router-dom";
import { ThunkDispatch } from "redux-thunk";
import { IAnalytics } from "../../types/analytics";

import { IUser } from '../../types/user';
import { IPosts } from '../../types/posts';
import { IGlobalErrorState } from '../../types/global_error';
import { IBreadItem } from '../../types/bread_crumb';
import { getQuestions, getAdvicesUser, setCategory } from '../../reducers/modules/questions/questions';
import { List } from '../../components/list/list';
import { LoadError } from '../../components/load_error/load_error';
import { BreadCrumbs } from '../bread_crumbs/bread_crumbs';
import { Aside } from '../aside/aside';
import { setBreadCrumb } from '../../reducers/modules/bread_crumbs/bread_crumbs';
import { saveScroll } from '../../reducers/modules/scroll/scroll';
import { IScroll } from '../../types/scroll';
import { getDiscussed } from '../../reducers/modules/discussed/discussed';
import { getRegions } from '../../reducers/modules/regions/regions';
import { BlogListItem } from '../../components/blog_list_item/blog_list_item';
import { scrollComments } from '../../reducers/modules/scroll_comment/scroll_comment';
import { IConfig } from '../../types/config';
import { combineDataLayerObject } from '../../../app/helpers/combine_data_layer_object';
import { getBreadCrumbsNames } from '../../../app/helpers/breadcrumbs_helper';
import { IApplicationState, IThunkContext, TReduxActions } from "../../types/redux";

import s from './questions.css';

interface IProps {
  user: IUser;
  questions: IPosts;
  scroll: IScroll;
  globalError: IGlobalErrorState;
  config: IConfig;
  deviceType: TDeviceType;
  analytics: IAnalytics;
}

interface IQuestionsProps extends IProps {
  routeCategory: string;
}

interface IDispatchProps {
  getQuestions(params: any): void;
  getRegions(scheme: string, host: string): void;
  getAdvicesUser(id: string): void;
  setCategory(category: string): void;
  setBreadCrumb(path: IBreadItem[]): void;
  getDiscussed(regionID: string): void;
  saveScroll(item: any): void;
  scrollComments(): void;
}

export const setCrumbsQuestions = (tag: string, search: string, category: string) => {
  const crumbs: IBreadItem[] = [
    {path: '/magazine'},
    {path: '/forum-rieltorov'},
  ];

  if (!!tag) {
    const name = tag[0].toUpperCase() + tag.substr(1);

    crumbs.push({
      path: tag,
      url: `/forum-rieltorov?tag=${tag}`,
      name,
    });
  } else if (!!search) {
    crumbs.push({
      path: search,
      url: `/voprosy?search=${search}`,
      name: 'Поиск',
    });
  } else if (!!category) {
    crumbs.push({path: `q-${category}`});
  }

  return crumbs;
}

const QuestionsContainer: React.FC<IQuestionsProps & IDispatchProps & RouteComponentProps> = props => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const tag = query.get("tag") || '';
  const search = query.get("search") || '';
  const ordering = query.get("ordering") || '-date_publish';

  const {config, routeCategory } = props;
  const deviceType = useDeviceType();
  const page = query.get("page") ? parseInt(query.get("page") as string) : 1;
  const regionID = config.regionID && config.regionID !== '0' ? config.regionID : '';
  const category = routeCategory ? routeCategory : query.get("category") || '';
  const authorID = query.get("author_id") || '';
  const commentUserID = query.get("comment_user_id") || '';

  React.useEffect(() => {
    const params = {page, ordering, tag, category, authorID, commentUserID, search, regionID};

    const crumbs = setCrumbsQuestions(tag, search, category);

    if (typeof window !== 'undefined') {
      ca('pageviewSite', combineDataLayerObject(props.analytics, getBreadCrumbsNames(crumbs), deviceType));
    }

    props.getQuestions(params);
    props.getRegions(config.regionsApiSchemeNode, config.regionsApiHostNode);
    props.setBreadCrumb(crumbs);
    props.getDiscussed(regionID);
    if (commentUserID) {
      props.getAdvicesUser(commentUserID);
    }
    props.setCategory(category);
  }, [tag, search, ordering, page, regionID, category, authorID, commentUserID]);

  const setAuthorBreadcrumb = (
    authorName: string,
    authorID: string,
  ) => {
    const crumbs: IBreadItem[] = [
      {path: '/magazine'},
      {path: '/forum-rieltorov'},
      {
        path: authorName,
        url: `/forum-rieltorov?author_id=${authorID}`,
        name: authorName,
      },
    ];

    props.setBreadCrumb(crumbs);
  };

  function loadMore(pageN: number) {
    const page = pageN || 1;
    const category = props.questions.currentCategory || '';
    const regionID = props.config.regionID && props.config.regionID !== '0' ? props.config.regionID : '';
    const authorID = query.get("author_id") || '';
    const commentUserID = query.get("comment_user_id") || '';
    const params = { page, ordering, tag, category, authorID, commentUserID, search, regionID };

    return props.getQuestions(params);
  }

  function seoHideBlock() {
    return (
      <span className={s['articles__schema']}>
        <span itemProp="headline">Советы риэлторов и других специалистов недвижимости России - ЦИАН</span>
        <span itemProp="description">
          ЦИАН - форум о недвижимости, советы риэлторов и других специалистов недвижимости. Задать вопрос риэлтору.
        </span>
      </span>
    );
  }

  const getUserInfo = () => {
    const { items, user: authorList } = props.questions;
    const isEmpty = items.length <= 0;

    if (query.get("author_id") && !isEmpty && items[0].attributes.authorObject) {
      return {
        authorName: items[0].attributes.authorObject!.fullName,
        specialistLink: items[0].attributes.authorObject!.specialistLink,
      };
    } else if (authorList && query.get("comment_user_id") && !isEmpty) {
      return {
        authorName: authorList.fullName,
        specialistLink: authorList.specialistLink,
      };
    }

    return {
      authorName: '',
      specialistLink: '',
    };
  }

  const {
    items,
    pages_count: pagesCount,
    count: recordsCount,
  } = props.questions;
  const { item: selectedItem } = props.scroll;

  if (getUserInfo().authorName) {
    setAuthorBreadcrumb(
      getUserInfo().authorName || '',
      query.get("author_id") || query.get("comment_user_id") || '',
    );
  }

  return (
    <div className={s['articles-wrap']}>
      <div itemScope itemType="https://schema.org/Question" className={`${s['articles']} cg-col-16 cg-col-xs-16`}>
        {seoHideBlock()}
        <BreadCrumbs />
        {
          props.globalError.error ?
            <LoadError /> :
            <List
                currentUser={props.user}
                regionId={regionID}
                items={items}
                pagesCount={pagesCount}
                recordsCount={recordsCount}
                searchTag={tag}
                saveScroll={props.saveScroll}
                selectedItem={selectedItem}
                listItemClass={BlogListItem}
                scrollComments={props.scrollComments}
                author={getUserInfo().authorName}
                specialistLink={getUserInfo().specialistLink}
                search={search}
                onLoadMore={(page: number) => loadMore(page)} />
        }
      </div>
      <Aside />
    </div>
  );
}

function mapStateToProps(state: IApplicationState): IProps {
  const {
    questions,
    scroll,
    globalError,
    user,
    config,
    deviceType,
    analytics,
  } = state;

  return {
    questions,
    scroll,
    globalError,
    user,
    config,
    deviceType,
    analytics,
  };
}

function mapDispatchToProps(dispatch: ThunkDispatch<IApplicationState, IThunkContext, TReduxActions>): IDispatchProps {
  return {
    getQuestions: (params: any) => {
      dispatch(getQuestions(params));
    },
    getRegions: (scheme: string, host: string) => {
      dispatch(getRegions(scheme, host));
    },
    setCategory: (category: string) => {
      dispatch(setCategory(category));
    },
    setBreadCrumb: (path: IBreadItem[]) => {
      dispatch(setBreadCrumb(path));
    },
    getAdvicesUser: (id: string) => {
      dispatch(getAdvicesUser(id));
    },
    getDiscussed: (regionID: string) => {
      dispatch(getDiscussed(regionID));
    },
    saveScroll: (item: any) => {
      dispatch(saveScroll(item));
    },
    scrollComments: () => {
      dispatch(scrollComments());
    },
  }
}

const mergePropsSpecialist = (stateProps: IProps, dispatchProps: IDispatchProps, ownProps: RouteComponentProps): IQuestionsProps & IDispatchProps & RouteComponentProps => {
  return Object.assign({routeCategory: 'specialist'}, stateProps, dispatchProps, ownProps)
}

const mergePropsMarketTrend = (stateProps: IProps, dispatchProps: IDispatchProps, ownProps: RouteComponentProps): IQuestionsProps & IDispatchProps & RouteComponentProps => {
  return Object.assign({routeCategory: 'market_trend'}, stateProps, dispatchProps, ownProps)
}

const mergePropsCityRealty = (stateProps: IProps, dispatchProps: IDispatchProps, ownProps: RouteComponentProps): IQuestionsProps & IDispatchProps & RouteComponentProps => {
  return Object.assign({routeCategory: 'city_realty'}, stateProps, dispatchProps, ownProps)
}

const mergePropsCommerceRealty = (stateProps: IProps, dispatchProps: IDispatchProps, ownProps: RouteComponentProps): IQuestionsProps & IDispatchProps & RouteComponentProps => {
  return Object.assign({routeCategory: 'commerce_realty'}, stateProps, dispatchProps, ownProps)
}

const mergePropsCountryRealty = (stateProps: IProps, dispatchProps: IDispatchProps, ownProps: RouteComponentProps): IQuestionsProps & IDispatchProps & RouteComponentProps => {
  return Object.assign({routeCategory: 'country_realty'}, stateProps, dispatchProps, ownProps)
}

const mergePropsForeignRealty = (stateProps: IProps, dispatchProps: IDispatchProps, ownProps: RouteComponentProps): IQuestionsProps & IDispatchProps & RouteComponentProps => {
  return Object.assign({routeCategory: 'foreign_realty'}, stateProps, dispatchProps, ownProps)
}

const mergePropsHypothec = (stateProps: IProps, dispatchProps: IDispatchProps, ownProps: RouteComponentProps): IQuestionsProps & IDispatchProps & RouteComponentProps => {
  return Object.assign({routeCategory: 'hypothec'}, stateProps, dispatchProps, ownProps)
}

export const Questions = connect(mapStateToProps, mapDispatchToProps)(QuestionsContainer);
export const SpecQuestions = connect(mapStateToProps, mapDispatchToProps, mergePropsSpecialist)(QuestionsContainer);
export const MarketQuestions = connect(mapStateToProps, mapDispatchToProps, mergePropsMarketTrend)(QuestionsContainer);
export const CityQuestions = connect(mapStateToProps, mapDispatchToProps, mergePropsCityRealty)(QuestionsContainer);
export const CommQuestions = connect(mapStateToProps, mapDispatchToProps, mergePropsCommerceRealty)(QuestionsContainer);
export const CountryQuestions = connect(mapStateToProps, mapDispatchToProps, mergePropsCountryRealty)(QuestionsContainer);
export const ForeignQuestions = connect(mapStateToProps, mapDispatchToProps, mergePropsForeignRealty)(QuestionsContainer);
export const HypothecQuestions = connect(mapStateToProps, mapDispatchToProps, mergePropsHypothec)(QuestionsContainer);
